import React from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { Environments } from "@emisgroup/cohort-builder-kit";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { CreateScheduleSlice } from "../../CreateSchedule/redux";
import { ISchedueListResponse,
  ScheduleDetails,
  IScheduleListType,
  SchedulingModes
} from "../../CreateSchedule/redux/Types";
import { useUser } from "../../../../../AuthContext";
import { ScheduleTableView } from "./ScheduleTableView";
import { ScheduleEmptyState } from "./ScheduleEmptyState.view";
import { populateTableData } from "./helper";
import { GetScheduleList } from "./Scripts";

/**
 * Responsible for rendering the schedule table component
 */
export const ScheduleTable: React.FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [
    extractState,
    scheduling,
    progressSpinnerData
  ] = useSelector((state: IRootState) => [
    state.extractState,
    state.scheduling,
    state.progressSpinnerData
  ], shallowEqual);
  const selectedExtractGuid = extractState.selectedExtract?.extractGuid;
  const scheduleList = scheduling?.scheduleList as unknown as IScheduleListType[];
  const { user } = useUser();
  const env = process.env.APP_TARGET_ENV as Environments;

  // Method for getting currently selected schedule details with schedule instances
  const onRowSelected = (e: React.SyntheticEvent<any, Event>): void => {
    const scheduleNameWithBadge = e.currentTarget.children[0].innerText;
    const scheduleName = scheduleNameWithBadge.split("\n");
    const currentScheduleList = scheduleList.filter((x: IScheduleListType) =>
      x.scheduleName === scheduleName[0]);
    // Destructing the schedule entries and schedule instances and dispatching separately
    const { scheduleInstances, ...scheduleEntries } = currentScheduleList[0];

    const scheduleEntriesDetails: ScheduleDetails = {
      extractGuid: scheduleEntries?.artefactGuid,
      scheduleGuid: scheduleEntries?.id,
      scheduleName: scheduleEntries?.scheduleName,
      activeInLive: scheduleEntries?.isActive,
      recurrenceType: scheduleEntries?.recurrenceType,
      recurrenceRunTime: String(scheduleEntries?.recurrenceData?.recurrenceRunTime),
      recurrenceRangeStart: String(scheduleEntries?.recurrenceData?.recurrenceRangeStart),
      ...(scheduleEntries?.recurrenceData?.recurrenceRangeEnd && {
        recurrenceRangeEnd: scheduleEntries?.recurrenceData?.recurrenceRangeEnd,
      }),
      ...(scheduleEntries?.recurrenceData?.recurrenceNoOfOccurrences && {
        recurrenceNoOfOccurrences: String(scheduleEntries?.recurrenceData?.recurrenceNoOfOccurrences),
      }),
      recurrenceData: {
        ...(scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.month && {
          month: scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.month,
        }),
        ...(scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.day && {
          day: scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.day,
        }),
        ...(scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.selectedWeekDays && {
          selectedWeekDays: scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.selectedWeekDays,
        }),
        ...(scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.calculation && {
          calculation: scheduleEntries?.recurrenceData?.recurrenceCalculationMeta?.calculation,
        }),
      },
    };

    if (scheduleEntries.recurrenceData.recurrenceRangeEnd) {
      dispatch(CreateScheduleSlice.actions.setIsEndByEnabled(true));
      dispatch(CreateScheduleSlice.actions.setIsEndAfterEnabled(false));
    }
    else {
      dispatch(CreateScheduleSlice.actions.setIsEndAfterEnabled(true));
      dispatch(CreateScheduleSlice.actions.setIsEndByEnabled(false));
    }
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails(scheduleEntriesDetails));
    dispatch(CreateScheduleSlice.actions.setscheduleInstances(scheduleInstances));
    dispatch(CreateScheduleSlice.actions.setScheduleListPage(true));
    dispatch(CreateScheduleSlice.actions.seSchedulingMode(SchedulingModes.VIEW));
  };

  React.useEffect(() => {
    if(selectedExtractGuid) {
      GetScheduleList(dispatch, translate, selectedExtractGuid, env, user?.token);
    }
  }, [selectedExtractGuid]);

  // Unmounting component
  React.useEffect(() => {
    return (): void => {
      dispatch(CreateScheduleSlice.actions.setScheduleList([] as unknown as ISchedueListResponse));
    };
  }, []);

  return (
    <div className="ebt-extract-schedule__table">
      { (scheduleList?.["length"] > 0 && progressSpinnerData.show === false) ?
        <ScheduleTableView
          scheduleList={populateTableData(scheduleList as unknown as IScheduleListType[])}
          onRowSelected={onRowSelected} /> :
        (progressSpinnerData.show === false && <ScheduleEmptyState />)}
    </div>
  );
};
